import { ref } from 'vue'

// Error code for CAPTCHA_SESSION_EXPIRED from backend
export const CAPTCHA_SESSION_EXPIRED_CODE = 11011
export const CAPTCHA_SESSION_EXPIRED_ERROR = 'CAPTCHA_SESSION_EXPIRED'

// Error code for EMAIL_ALREADY_EXISTS - redirect to login
export const EMAIL_ALREADY_EXISTS_CODE = 11012

// Error code for ACCOUNT_LOGIN_FAIL - wrong email or password
export const LOGIN_FAIL_CODE = 101

export const LOGIN_SESSION_EXPIRED = 22022

// State for the captcha popup
const isPopupVisible = ref(false)
const popupResolve = ref<((token: string) => void) | null>(null)
const popupReject = ref<((error: Error) => void) | null>(null)

/**
 * Check if error is a captcha session expired error
 */
export const isCaptchaSessionExpiredError = (error: any): boolean => {
    // Check various error response formats
    const errorCode = error?.response?.data?.error_code ??
        error?.response?.data?.errorCode ??
        error?.error_code ??
        error?.errorCode ??
        error?.code

    const errorMessage = error?.response?.data?.error_message ??
        error?.response?.data?.message ??
        error?.message ?? ''

    return errorCode === CAPTCHA_SESSION_EXPIRED_CODE ||
        errorCode === 11011 ||
        errorCode === '11011' ||
        (typeof errorMessage === 'string' && errorMessage.includes(CAPTCHA_SESSION_EXPIRED_ERROR))
}

/**
 * Check if error is an email already exists error (user should login instead)
 */
export const isEmailAlreadyExistsError = (error: any): boolean => {
    const errorCode = error?.response?.data?.error_code ??
        error?.response?.data?.errorCode ??
        error?.error_code ??
        error?.errorCode ??
        error?.code

    return errorCode === EMAIL_ALREADY_EXISTS_CODE ||
        errorCode === 11012 ||
        errorCode === '11012'
}

/**
 * Check if error is a login fail error (wrong email or password)
 */
export const isLoginFailError = (error: any): boolean => {
    const errorCode = error?.response?.data?.error_code ??
        error?.response?.data?.errorCode ??
        error?.error_code ??
        error?.errorCode ??
        error?.code

    return errorCode === LOGIN_FAIL_CODE ||
        errorCode === 101 ||
        errorCode === '101'
}

/**
 * Show captcha popup and return a promise that resolves with the token
 */
export const requestCaptchaToken = (): Promise<string> => {
    return new Promise((resolve, reject) => {
        popupResolve.value = resolve
        popupReject.value = reject
        isPopupVisible.value = true
    })
}

/**
 * Complete captcha verification (called from popup)
 */
export const completeCaptchaVerification = (token: string): void => {
    if (popupResolve.value) {
        popupResolve.value(token)
        popupResolve.value = null
        popupReject.value = null
    }
    isPopupVisible.value = false
}

/**
 * Cancel captcha verification
 */
export const cancelCaptchaVerification = (): void => {
    if (popupReject.value) {
        popupReject.value(new Error('Captcha verification cancelled'))
        popupResolve.value = null
        popupReject.value = null
    }
    isPopupVisible.value = false
}

/**
 * Get reactive popup visibility state
 */
export const getPopupVisibility = () => isPopupVisible

/**
 * Retry a request with a fresh captcha token
 * This replaces captcha-session with g-recaptcha-response
 */
export const retryWithFreshCaptcha = async <T>(
    requestFn: (captchaToken: string) => Promise<T>
): Promise<T> => {
    const token = await requestCaptchaToken()
    return requestFn(token)
}
